import React, { useState, useEffect } from "react";
import API from "@/lib/API";
import { estimationService } from "../../../services/estimation_service";
import { statusRevertService } from "../../contacts/services/statusRevertService";
import { ReceiptIndianRupee, Package, CheckSquare, IndianRupee, FileQuestion } from "lucide-react";
import { StatCard } from "@/components/ui";

export default function ApprovalsStatsCards() {
  const [stats, setStats] = useState({
    expenses: 0,
    materials: 0,
    quotation: 0,
    reverts: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      setLoading(true);
      try {
        const [
          expensesRes,
          materialsRes,
          budgetRes,
          revertRes
        ] = await Promise.allSettled([
          API.get("/general-expenses"),
          API.get("/final-inventory/material-requests?ownerView=true"),
          estimationService.getEstimations({ status: 'PENDING_APPROVAL' }),
          statusRevertService.getPendingRequests()
        ]);

        if (!isMounted) return;

        let expensesCount = 0;
        let materialsCount = 0;
        let quotationCount = 0;
        let revertCount = 0;

        // Expenses
        if (expensesRes.status === "fulfilled") {
          const data = expensesRes.value.data?.data || expensesRes.value.data || expensesRes.value || [];
          expensesCount = data.filter(e => {
            const s = String(e.status || "pending").toLowerCase();
            return s === "pending" || s === "pending_approval";
          }).length;
        }

        // Materials (Order Approvals)
        if (materialsRes.status === "fulfilled") {
          const data = materialsRes.value.data?.data || materialsRes.value.data || materialsRes.value || [];
          materialsCount = data.filter(m => String(m.status).toLowerCase() === "pending" || String(m.status).toUpperCase() === "AWAITING_OWNER_APPROVAL").length;
        }

        // Quotation / Estimation
        if (budgetRes.status === "fulfilled") {
          const data = budgetRes.value?.data || budgetRes.value || [];
          if (Array.isArray(data)) {
            quotationCount = data.filter(q => String(q.status).toUpperCase() === "PENDING_APPROVAL").length;
          }
        }

        // Reverts / Stage Revert
        if (revertRes.status === "fulfilled") {
          const data = revertRes.value?.data || revertRes.value || [];
          revertCount = data.filter(r => String(r.status).toLowerCase() === "pending").length;
        }

        setStats({
          expenses: expensesCount,
          materials: materialsCount,
          quotation: quotationCount,
          reverts: revertCount
        });
      } catch (err) {
        console.error("Failed to fetch approval stats:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchStats();

    return () => {
      isMounted = false;
    };
  }, []);

  const statCards = [
    { label: "Expense Approvals", count: stats.expenses, icon: IndianRupee, color: "text-blue-500", bg: "bg-blue-50" },
    { label: "Order Approvals", count: stats.materials, icon: Package, color: "text-amber-500", bg: "bg-amber-50" },
    { label: "Discount Approvals", count: stats.quotation, icon: IndianRupee, color: "text-emerald-500", bg: "bg-emerald-50" },
    // { label: "Stage Revert", count: stats.reverts, icon: FileQuestion, color: "text-purple-500", bg: "bg-purple-50" }
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 h-24 animate-pulse flex items-center gap-4">
            <div className="w-12 h-12 bg-slate-100 rounded-full"></div>
            <div className="flex flex-col gap-2 flex-1">
              <div className="h-4 bg-slate-100 rounded w-1/2"></div>
              <div className="h-6 bg-slate-100 rounded w-1/4"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 relative group/stats">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
        {statCards.map((card, idx) => (
          <StatCard
            key={idx}
            variant="luxury"
            title={card.label}
            value={card.count}
            sub="Pending Requests"
            topColor="border-t-[#C91D25]"
            iconHoverColor="group-hover:text-[#ffa000]"
            illustration={<card.icon className="w-10 h-10" strokeWidth={2} />}
          />
        ))}
      </div>
    </div>
  );
}

